import { useRef } from "react";
import { motion } from "framer-motion";
import { TICKETS, VIP_TICKET } from "@/lib/data";
import { fadeUp, inViewOnce } from "@/lib/motion";
import { track } from "@/lib/track";
import { Check, Sparkle } from "lucide-react";

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-40 border-t border-[color:var(--line)] bg-[color:var(--bone-2)]"
    >
      <div className="mx-auto max-w-[1360px] px-5 md:px-8">
        <div className="flex items-center justify-between mb-14 md:mb-20">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink-3)]">
            Ch. 05 — Pricing
          </span>
          <span className="font-mono text-[11px] tabular-nums text-[color:var(--ink-3)]">
            §005 / 010
          </span>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-14 md:mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={inViewOnce}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-8 font-serif text-[42px] sm:text-[62px] md:text-[78px] leading-[0.98] tracking-[-0.03em] font-medium"
          >
            Choose your{" "}
            <em className="italic font-normal text-[color:var(--copper-2)]">
              certification experience.
            </em>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={inViewOnce}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="col-span-12 md:col-span-4 text-[15px] md:text-[16px] leading-[1.55] text-[color:var(--ink-2)]"
          >
            Select the level of access and support that best fits your goals.
          </motion.p>
        </div>

        {/* Cards grid: Standard + VIP */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-[900px] mx-auto">
          {TICKETS.map((t, i) => (
            <StandardCard key={t.id} ticket={t} index={i} />
          ))}
          <VipCard />
        </div>

        {/* Disclaimer */}
        <p className="mt-10 max-w-[720px] text-[12px] leading-[1.65] text-[color:var(--ink-3)]">
          Prices shown in USD. Eventbrite fees and applicable taxes may be added
          at checkout. Ticket names and prices are governed by the live
          Eventbrite inventory. If a tier has sold out, the checkout will
          indicate the next available option.
        </p>
      </div>
    </section>
  );
}

function StandardCard({ ticket, index }) {
  return (
    <motion.article
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={inViewOnce}
      custom={index}
      data-testid={`ticket-card-${ticket.id}`}
      className="relative bg-[color:var(--bone)] border border-[color:var(--line-strong)] p-6 md:p-7 flex flex-col min-h-[540px]"
    >
      {ticket.tag && (
        <span className="absolute top-4 right-4 font-mono text-[9px] uppercase tracking-[0.2em] text-[color:var(--copper)] bg-[color:var(--copper-tint)] px-2 py-1 rounded-full">
          {ticket.tag}
        </span>
      )}
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--ink-3)]">
        Pass · 0{index + 1}
      </p>
      <h3 className="mt-4 font-serif text-[26px] md:text-[30px] leading-[1.1] font-medium text-[color:var(--ink)]">
        {ticket.name}
      </h3>

      <div className="mt-6">
        {ticket.original && (
          <span className="mr-2 font-mono text-[13px] line-through text-[color:var(--ink-3)]">
            ${ticket.original}
          </span>
        )}
        <span className="font-serif text-[44px] leading-none font-medium tabular-nums text-[color:var(--ink)]">
          ${ticket.price}
        </span>
        <span className="ml-2 font-sans text-[13px] text-[color:var(--ink-3)]">USD</span>
      </div>
      {ticket.deadline && (
        <p className="mt-2 font-mono text-[11px] text-[color:var(--ink-3)]">
          {ticket.deadline}
        </p>
      )}

      <ul className="mt-6 space-y-2.5 flex-1">
        {ticket.inclusions.map((it) => (
          <li key={it} className="flex items-start gap-2 text-[13.5px] text-[color:var(--ink-2)]">
            <Check size={14} className="mt-0.5 text-[color:var(--copper)] shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>

      <a
        href={ticket.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`ticket-cta-${ticket.id}`}
        onClick={() =>
          track("ticket_click", {
            tier: ticket.id,
            price: ticket.price,
            location: "pricing",
          })
        }
        className="mt-6 btn-outline w-full justify-center !py-3"
      >
        Choose Your Pass
        <span>→</span>
      </a>
    </motion.article>
  );
}

function VipCard() {
  const ref = useRef(null);
  const handleMove = (e) => {
    const node = ref.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * 100;
    const my = ((e.clientY - rect.top) / rect.height) * 100;
    node.style.setProperty("--mx", `${mx}%`);
    node.style.setProperty("--my", `${my}%`);
  };

  return (
    <motion.article
      ref={ref}
      onMouseMove={handleMove}
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={inViewOnce}
      custom={4}
      data-testid="ticket-card-vip"
      className="spotlight relative bg-[color:var(--ink)] text-[color:var(--bone)] border border-[color:var(--ink)] p-6 md:p-7 flex flex-col min-h-[540px] overflow-hidden"
    >
      {/* Corner ornaments */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-3 left-3 w-3 h-3 border-t border-l border-[color:var(--copper)]" />
        <div className="absolute top-3 right-3 w-3 h-3 border-t border-r border-[color:var(--copper)]" />
        <div className="absolute bottom-3 left-3 w-3 h-3 border-b border-l border-[color:var(--copper)]" />
        <div className="absolute bottom-3 right-3 w-3 h-3 border-b border-r border-[color:var(--copper)]" />
      </div>

      <span className="absolute top-4 right-4 font-mono text-[9px] uppercase tracking-[0.2em] text-[color:var(--ink)] bg-[color:var(--copper)] px-2 py-1 rounded-full flex items-center gap-1">
        <Sparkle size={10} strokeWidth={2} />
        {VIP_TICKET.tag}
      </span>

      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--copper)]">
        Pass · 04 · Limited
      </p>
      <h3 className="mt-4 font-serif text-[28px] md:text-[32px] leading-[1.05] font-medium text-[color:var(--bone)]">
        {VIP_TICKET.name}
      </h3>

      <div className="mt-6">
        <span className="font-serif text-[44px] md:text-[52px] leading-none font-medium tabular-nums text-[color:var(--bone)]">
          $1,499
          <span className="text-[color:var(--copper)]">.99</span>
        </span>
        <span className="ml-2 font-sans text-[13px] text-[color:var(--bone)]/60">USD</span>
      </div>
      <p
        data-testid="vip-availability"
        className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--copper)]"
      >
        Limited to {VIP_TICKET.seats} participants
      </p>

      <ul className="mt-6 space-y-2.5 flex-1">
        {VIP_TICKET.inclusions.map((it) => (
          <li key={it} className="flex items-start gap-2 text-[13.5px] text-[color:var(--bone)]/85">
            <Check size={14} className="mt-0.5 text-[color:var(--copper)] shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>

      <p className="mt-5 text-[11px] leading-[1.55] text-[color:var(--bone)]/55">
        {VIP_TICKET.note}
      </p>

      <a
        href={VIP_TICKET.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="ticket-cta-vip"
        onClick={() =>
          track("vip_pass_click", {
            tier: "vip",
            price: VIP_TICKET.price,
            location: "pricing",
          })
        }
        className="mt-6 inline-flex items-center justify-center gap-2 w-full py-3 rounded-full bg-[color:var(--copper)] text-[color:var(--ink)] font-medium text-[14px] hover:bg-[color:var(--bone)] transition-colors"
      >
        Get the VIP Pass
        <span>→</span>
      </a>
      <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.24em] text-[color:var(--copper)]">
        Only 10 VIP Passes available
      </p>
    </motion.article>
  );
}
